import React from 'react';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Fab from '@mui/material/Fab';
import SearchIcon from '@mui/icons-material/Search';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { green } from '@mui/material/colors';
import {styled} from '@mui/system';

import {concatUrl} from '../helpers/url';
import history from '../helpers/history';
import {validId} from '../helpers/validate';
import {getCall, postCall} from '../helpers/request';
import GlobalContext from '../helpers/context';
import FormButton from '../component/atoms/FormButton';
import LinkButton from '../component/atoms/LinkButton';
import {checkUserRoles} from '../helpers/authentication';
import Autocomplete from '../component/Autocomplete';
import ResearcherId from '../component/molecule/ResearcherId';
import {getUrlParams} from '../helpers/location';


const StyledCurationProfileForm = styled(Paper)(({ theme }) =>({
    paddingLeft: theme.custom.paper.side_space,
    paddingRight: theme.custom.paper.side_space,
    paddingTop: theme.custom.paper.top_space,
    paddingBottom: theme.custom.paper.bottom_space,
    marginBottom: 30
}));

const StyledFormTitle = styled(Typography)({
    marginBottom: 20
});

const StyledFormControl = styled(FormControl)({
    minWidth: 200
});

const StyledAutocompleteFormControl = styled(StyledFormControl)({
    marginBottom: 50
});

const StyledErrorMessage = styled(Typography)(({ theme }) =>({
    color: theme.custom.color.error,
    fontWeight: theme.custom.font.bold,
    marginTop: 5,
    marginLeft: 0
}));

const StyledFab = styled(Fab)({
    height: '48px',
    width: '48px'
});

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) =>({
    fontSize: theme.custom.font.medium
}));

const StyledCircularProgress = styled(CircularProgress)({
    color: green[500],
    marginTop: 5,
    marginLeft: 5
});


class CurationProfileForm extends React.Component {

    constructor(props) {
        super(props);

        this.params = getUrlParams();
                
        this.state = {
            first_name: '',
            last_name: '',
            first_name_overwrite: '',
            last_name_overwrite: '',
            profile_full_name: '',
            computed_first_name: '',
            computed_last_name: '',
            researcher_id: '',
            search_is_running: false,
            form_errors: {},
            profile_id_exist: false,
            overwrite_info: '',
            organization_overwrite_id: '',
            organization_overwrite_label: '',
            delete_organization: false,
            save_and_select: false
        };

        this.form_data = {};

        this.is_mounted = false;
        this.accepted_roles = ['admin', 'manager', 'reviewer'];
        this.create_curation_profile_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'create');
        this.curation_profile_form_data_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'form_data');
        this.save_curation_profile_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'save');
        this.save_overwrites_url = concatUrl(window.ROOT_URL, 'api', 'profile', 'save_overwrites');
        this.search_profile_url = concatUrl(window.ROOT_URL, 'api', 'profile', 'get_name');
        this.search_org_url = concatUrl(window.ROOT_URL, 'api', 'completion', 'search_org');

        this.form_title = this.params.id ? 'Edit curation profile' : 'Create curation profile';
        this.save_button = this.params.id ? 'Save' : 'Create';

    }

    componentDidMount(){
        this.is_mounted = true;
        this.props.updatedCallback();
        if (this.params.id && this.params.workspace_id){
            this.loadCurationProfileData();
        }else{
            this.context.checkUserAccess(this.conditionCallback);
        }
    }

    componentDidUpdate(){
        this.props.updatedCallback();
    }

    componentWillUnmount(){
        this.is_mounted = false;
    }

    conditionCallback = (user) => {
        return checkUserRoles(user, this.accepted_roles);
    };

    formValidated = () => {
        let form_errors = {
            'last_name': this.state.last_name.trim().length === 0 ? 'Please enter a last name' : null,
            'researcher_id': this.state.researcher_id !== '' && !validId(this.state.researcher_id) ? 'Invalid researcher ID' : null,
        };

        this.form_data = {
            last_name: this.state.last_name.trim(),
            first_name: this.state.first_name.trim(),
            researcher_id: this.state.researcher_id,
            workspace_id: this.params.workspace_id,
        };

        if(this.params.id){
            // edit existing curation profile
            this.form_data['id'] = this.params.id;
        }

        this.setState({form_errors: form_errors});
        let form_is_valid = true;

        /*eslint-disable no-unused-vars */
        for (const key in form_errors){
            if (form_errors[key]){
                form_is_valid = false;
                break;
            }
        }
        /*eslint-enable no-unused-vars */

        return form_is_valid;
    };

    overwriteFormValidated = () => {
        let last_name_overwrite = this.state.last_name_overwrite.trim();
        let first_name_overwrite = this.state.first_name_overwrite.trim();


        let form_errors = {
            'last_name_overwrite': last_name_overwrite !== '' && last_name_overwrite.trim().length < 2 ? 'Please enter a last name of at least 2 characters' : null
        };

        this.form_data = {
            last_name: last_name_overwrite,
            first_name: first_name_overwrite,
            organization_overwrite_id: this.state.organization_overwrite_id,
            delete_organization: this.state.delete_organization,
            workspace_id: this.params.workspace_id,
            curation_profile_id: this.params.id
        };

        this.setState({form_errors: form_errors});
        let form_is_valid = true;

        /*eslint-disable no-unused-vars */
        for (const key in form_errors){
            if (form_errors[key]){
                form_is_valid = false;
                break;
            }
        }
        /*eslint-enable no-unused-vars */

        return form_is_valid;
    };

    manageRedirect = (id) => {
        if (this.state.save_and_select){
            this.setState({save_and_select: true});
            history.push(`/workspaces/curation_profiles/details?id=${id}&workspace_id=${this.params.workspace_id}`);
            document.location.reload();
        }else{
            history.push(`/workspaces/details?id=${this.params.workspace_id}`);
            document.location.reload();
        }
    };

    // +++ create/save curation profile +++++++++++++++++++++++++++++++++++++++++++++++

    saveCurationProfileFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not create/save the curation profile data.');
    };

    saveCurationProfileDone = (data) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (data && data.profile_exist_msg){
            this.context.displayGlobalWarning(data.profile_exist_msg);
        }
        this.manageRedirect(data.id);
    };

    saveCurationProfile = () => {
        if (this.formValidated()){
            const url = this.params.id ? this.save_curation_profile_url : this.create_curation_profile_url;
            postCall(url, this.form_data, this.saveCurationProfileDone, this.saveCurationProfileFailed);
        }
    };

    saveAndSelectCurationProfile = () => {
        this.setState({save_and_select: true});
        this.saveCurationProfile();
    };

    // +++ save overwrites +++++++++++++++++++++++++++++++++++++++++++++++

    saveOverwritesFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not save the profile overwrites.');
    };

    saveOverwritesDone = (data) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.manageRedirect(data.id);
    };

    saveOverwrites = () => {
        if (this.overwriteFormValidated()){
            postCall(this.save_overwrites_url, this.form_data, this.saveOverwritesDone, this.saveOverwritesFailed);
        }
    };

    saveAndSelectOverwrites = () => {
        this.setState({save_and_select: true});
        this.saveOverwrites();
    };


    // +++ load curation profile data +++++++++++++++++++++++++++++++++++++++++++++++

    loadCurationProfileDataFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not load curation profile data.');
    };

    loadCurationProfileDataDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (!data) {
            callback_failure();
        }else{
            let status_data = {
                profile_id: data.profile_id,
                researcher_id: data.researcher_id,
                first_name: data.first_name,
                last_name: data.last_name,
                computed_first_name: data.computed_first_name,
                computed_last_name: data.computed_last_name,
                computed_org: data.computed_org,
                current_org: data.current_org,
                profile_full_name: data.profile_full_name,
                first_name_overwrite: '',
                last_name_overwrite: '',
                latest_affiliation: '',
                profile_id_exist: data.profile_id > 0
            };

            if (data.overwrites){
                if (data.overwrites.first_name){
                    status_data['first_name_overwrite'] = data.overwrites.first_name;
                }
                if (data.overwrites.last_name){
                    status_data['last_name_overwrite'] = data.overwrites.last_name;
                }
                if (data.overwrites.latest_affiliation){
                    status_data['latest_affiliation'] = data.overwrites.latest_affiliation;
                    status_data['organization_overwrite_id'] = data.overwrites.latest_affiliation.id;
                    status_data['organization_overwrite_label'] = data.overwrites.latest_affiliation.label;
                }
                if(data.overwrites.organization_assignment_removed === true){
                    status_data['delete_organization'] = true;
                }
            }

            if (status_data.last_name_overwrite || status_data.first_name_overwrite){
                status_data['overwrite_info_name'] = `The original name is ${data.computed_last_name}, ${data.computed_first_name}.`;
            }

            if (status_data.latest_affiliation){
                status_data['overwrite_info_org'] = `The original organization is ${data.computed_org}.`;
            }

            this.setState(status_data);
        }
    };

    loadCurationProfileData = () => {
        getCall(`${this.curation_profile_form_data_url}?id=${this.params.id}&workspace_id=${this.params.workspace_id}`, this.loadCurationProfileDataDone,
            this.loadCurationProfileDataFailed);
    };


    // +++ search profile +++++++++++++++++++++++++++++++++++++++++++++++

    searchProfileFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.setState({
            search_is_running: false
        });
        this.context.displayGlobalError('Backend service failure: Search profile is not working.');
    };

    searchProfileDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.setState({
            search_is_running: false
        });
        if (!data) {
            callback_failure();
        }else{
            if(data.error && data.error === 'ConvertProfileIdError'){
                this.setState({form_errors: {researcher_id: 'Invalid researcher ID'}});
            }else{
                if(data.last_name){
                    this.setState({
                        first_name: data.first_name,
                        last_name: data.last_name
                    });
                }else{
                     this.setState({form_errors: {researcher_id: `No profile found with a search for ${this.state.researcher_id}`}});
                }
            }
        }
    };

    searchProfile = () => {
        if(!validId(this.state.researcher_id)){
            this.setState({form_errors: {researcher_id: 'Invalid researcher ID'}});
        }else{
            this.setState({
                form_errors: {},
                search_is_running: true
            });

            getCall(`${this.search_profile_url}?id=${this.state.researcher_id}`, this.searchProfileDone,
                this.searchProfileFailed);
        }
    };


    onOrgSelect = (suggestion) => {
        this.setState({
            organization_overwrite_id: suggestion.id,
            form_errors: {}
        });
    };

    resetOrgSelection = () => {
        this.setState({
            organization_overwrite_id: '',
            organization_overwrite_label: '',
            form_errors: {}
        });
    };

    selectionRequiredCallback = () => {
        this.setState({
            form_errors: {search: "You have to select one of the suggestions."}
        });
    };

    autoCompleteSearchFailedCallback = () => {
        this.context.displayGlobalError('Backend service failure: Could not run auto complete.');
    };

    showErrorMessage = (key) => {
        if (key in this.state.form_errors && this.state.form_errors[key]) {
            return (
                <StyledErrorMessage data-bt={`error_${key}`}>
                    {this.state.form_errors[key]}
                </StyledErrorMessage>
            );
        }
    };

    errorMode = (key) => {
        return key in this.state.form_errors && this.state.form_errors[key] !== null;
    };

    overwriteForm = () => {
        if(this.params.id) {
            if (this.state.profile_id) {
                return (
                    <StyledCurationProfileForm data-bt="overwrite_form">
                        <StyledFormTitle variant="h5">
                            Profile overwrites
                        </StyledFormTitle>
                        <p>
                            The current name of the profile ({<ResearcherId id={this.state.researcher_id} />}) is <strong>{this.state.profile_full_name}</strong>.
                            {this.state.overwrite_info_name && <span><br/>{this.state.overwrite_info_name}</span>}
                        </p>
                        <Grid container spacing={5}>
                            <Grid size={6}>
                                <StyledFormControl fullWidth={true}>
                                    <TextField
                                        error={this.errorMode('last_name_overwrite')}
                                        variant="outlined"
                                        id="last_name_overwrite"
                                        type="text"
                                        label="Last name"
                                        slotProps={{ htmlInput: {'data-bt': 'last_name_overwrite'} }}
                                        value={this.state.last_name_overwrite}
                                        onChange={(event) => {
                                            this.setState({last_name_overwrite: event.target.value});
                                        }}
                                    />
                                    {this.showErrorMessage('last_name_overwrite')}
                                </StyledFormControl>
                            </Grid>
                            <Grid size={6}>
                                <StyledFormControl fullWidth={true}>
                                    <TextField
                                        error={this.errorMode('first_name_overwrite')}
                                        variant="outlined"
                                        id="first_name_overwrite"
                                        type="text"
                                        label="First name"
                                        slotProps={{ htmlInput: {'data-bt': 'first_name_overwrite'} }}
                                        value={this.state.first_name_overwrite}
                                        onChange={(event) => {
                                            this.setState({first_name_overwrite: event.target.value});
                                        }}
                                    />
                                    {this.showErrorMessage('first_name_overwrite')}
                                </StyledFormControl>
                            </Grid>
                        </Grid>
                        <p>
                            The current organization of the profile ({<ResearcherId id={this.state.researcher_id} />}) is <strong>{this.state.current_org || 'not set'}</strong>.
                            {this.state.overwrite_info_org && <span><br/>{this.state.overwrite_info_org}</span>}
                        </p>
                        <Grid container spacing={5}>
                            <Grid size={6}>
                                <StyledAutocompleteFormControl fullWidth={true}>
                                    <Autocomplete id="search_org"
                                                  label="Organization"
                                                  value={this.state.organization_overwrite_label}
                                                  getSuggestionsUrl={this.search_org_url}
                                                  onSuggestionSelected={this.onOrgSelect}
                                                  onChange={this.resetOrgSelection}
                                                  selectionRequired={true}
                                                  selectionRequiredCallback={this.selectionRequiredCallback}
                                                  autoCompleteSearchFailedCallback={this.autoCompleteSearchFailedCallback}
                                    />
                                    {this.showErrorMessage('search')}
                                </StyledAutocompleteFormControl>
                                <StyledFormControlLabel
                                    key="delete_organization"
                                    control={
                                        <Checkbox
                                            checked={this.state.delete_organization}
                                            onChange={() => {this.setState({delete_organization: !this.state.delete_organization})}}
                                            value={true}
                                        />
                                    }
                                    label="Remove organization assignment"
                                />
                            </Grid>
                            <Grid size={12}>
                                <FormButton text={this.save_button + ' and select'} dataBt="overwrite_save_and_select"
                                              actionCallback={this.saveAndSelectOverwrites} />
                                <FormButton text="Save" dataBt="overwrite_save"
                                              actionCallback={this.saveOverwrites}/>
                                <LinkButton text="Cancel" dataBt="overwrite_cancel" linkData={{
                                    pathname: '/workspaces/details',
                                    search: `?id=${this.params.workspace_id}`
                                }}/>
                            </Grid>
                        </Grid>
                    </StyledCurationProfileForm>
                );
            }
            return (
               <StyledCurationProfileForm data-bt="profile_form">
                    <StyledFormTitle variant="h5">
                        Profile overwrites
                    </StyledFormTitle>
                   <p>
                       Overwrites are only possible on a profile but not on a curation profile.
                       To use the overwrites you have to assign a profile first.
                    </p>
               </StyledCurationProfileForm>
            );
        }
    };

    render() {
        return (
            <GlobalContext.Consumer>{() => (
                <form>
                    <StyledCurationProfileForm data-bt="curation_profile_form">
                        <StyledFormTitle variant="h5">
                            {this.form_title}
                        </StyledFormTitle>
                        <Grid container spacing={5}>
                            <Grid size={6}>
                                <StyledFormControl fullWidth={true}>
                                    <TextField
                                        error={this.errorMode('researcher_id')}
                                        disabled={this.state.profile_id_exist}
                                        variant="outlined"
                                        id="researcher_id"
                                        type="text"
                                        label="Researcher ID - can't be modified after it was assigned"
                                        slotProps={{ htmlInput: {'data-bt': 'researcher_id'} }}
                                        placeholder="ur.1234567.89"
                                        value={this.state.researcher_id}
                                        onChange={(event) => {
                                            this.setState({researcher_id: event.target.value});
                                        }}
                                    />
                                    {this.showErrorMessage('researcher_id')}
                                </StyledFormControl>
                            </Grid>
                            <Grid size={6}>
                                {this.state.search_is_running && <StyledCircularProgress />}
                                {!this.state.search_is_running && <StyledFab aria-label="add" color="primary"
                                                                       onClick={this.searchProfile}
                                                                       data-bt="search_profile_button"
                                                                       disabled={!this.state.researcher_id}>
                                    <SearchIcon />
                                </StyledFab> }
                            </Grid>
                            <Grid size={6}>
                                <StyledFormControl fullWidth={true}>
                                    <TextField
                                        required
                                        error={this.errorMode('last_name')}
                                        variant="outlined"
                                        id="last_name"
                                        type="text"
                                        label="Last name"
                                        slotProps={{ htmlInput: {'data-bt': 'last_name'} }}
                                        value={this.state.last_name}
                                        onChange={(event) => {
                                            this.setState({last_name: event.target.value});
                                        }}
                                    />
                                    {this.showErrorMessage('last_name')}
                                </StyledFormControl>
                            </Grid>
                            <Grid size={6}>
                                <StyledFormControl fullWidth={true}>
                                    <TextField
                                        error={this.errorMode('first_name')}
                                        variant="outlined"
                                        id="first_name"
                                        type="text"
                                        label="First name"
                                        slotProps={{ htmlInput: {'data-bt': 'first_name'} }}
                                        value={this.state.first_name}
                                        onChange={(event) => {
                                            this.setState({first_name: event.target.value});
                                        }}
                                    />
                                </StyledFormControl>
                            </Grid>
                            <Grid size={12}>
                                <FormButton text={this.save_button + ' and select'} dataBt="create_and_select" actionCallback={this.saveAndSelectCurationProfile} />
                                <FormButton text={this.save_button} dataBt="create" actionCallback={this.saveCurationProfile} />
                                <LinkButton text="Cancel" dataBt="cancel" linkData={{pathname: '/workspaces/details', search: `?id=${this.params.workspace_id}`}} />
                            </Grid>
                        </Grid>
                    </StyledCurationProfileForm>
                    {this.overwriteForm()}
                </form>
            )}
            </GlobalContext.Consumer>
        );
    }
}

CurationProfileForm.contextType = GlobalContext;

CurationProfileForm.propTypes = {
    updatedCallback: PropTypes.func
};

export default CurationProfileForm;
